import React from 'react';
import { FormattedCandidate } from 'lib/CandidatePortraits/CandidatePortraits';
import { useTranslation } from 'react-i18next';

interface Options {
  showOnlyElected?: boolean;
  showElectedColumn?: boolean;
  showCountingCircleColumn?: boolean;
}

interface FilterProps {
  search?: string;
  selectedLists?: string[];
  selectedDistricts?: string[];
}

const normalizeString = (name: string) => {
  // treat all special characters, e.g. é or ö as their base character, e.g. e or o
  return name
    ?.toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
};

export const useGetCandidatesTableData = (
  formattedCandidates: FormattedCandidate[],
  options?: Options,
  filterProps?: FilterProps,
) => {
  // in useGetCandidatesCanton, we add placeholder-candidates for open seats.
  // We don't want to show these in the elected-table - only the real candidates.
  // open-seat entries have no candidateNumber
  const candidatesWithoutOpenSeats = formattedCandidates?.filter(
    (candidate) => candidate.candidateNumber,
  );

  const { t } = useTranslation('wap');
  const { search = '', selectedLists = [], selectedDistricts = [] } = filterProps || {};

  if (!candidatesWithoutOpenSeats) {
    return { data: null };
  }

  const {
    showOnlyElected = false,
    showElectedColumn = false,
    showCountingCircleColumn = false,
  } = options ?? {};

  const candidateList = showOnlyElected
    ? candidatesWithoutOpenSeats.filter((candidate) => candidate.elected)
    : candidatesWithoutOpenSeats;

  const distinctLists = [
    ...new Set(
      candidateList.map((candidate) => `${candidate.list} ${candidate.listName}`),
    ),
  ];
  const distinctDistricts = [
    ...new Set(candidateList.map((candidate) => candidate.countingCircle)),
  ];

  const data = {
    head: [
      { title: t('list'), width: 15, sortMethod: 'default' },
      {
        title: (
          <>
            <span className="proportionalelectiondetails__candidatenumber--desktop">
              {t('candidate_no')}
            </span>
            <span className="proportionalelectiondetails__candidatenumber--mobile">
              {t('candidate_no_short')}
            </span>
          </>
        ),
        width: 5,
        sortMethod: 'default',
      },
      { title: `${t('name')} ${t('first_name')}`, width: 25, sortMethod: 'default' },
      { title: `${t('new')} / ${t('is_up_to_now')}`, width: 15, sortMethod: 'default' },
      { title: t('city'), width: 20, sortMethod: 'default' },
      { title: t('votes'), width: 10, sortMethod: 'number' },
    ],
    body: {
      rows: candidateList
        .filter(({ firstName, lastName, list, listName, countingCircle }) => {
          const hasSearchMatch = [
            firstName,
            lastName,
            `${firstName} ${lastName}`,
            `${lastName} ${firstName}`,
          ].some((value) => {
            return normalizeString(value).includes(normalizeString(search));
          });
          const hasListMatch =
            selectedLists.length === 0 || selectedLists.includes(`${list} ${listName}`);
          const hasDistrictMatch =
            selectedDistricts.length === 0 || selectedDistricts.includes(countingCircle);
          return hasSearchMatch && hasListMatch && hasDistrictMatch;
        })
        .map((candidate) => {
          let upToNow = candidate.isUpToNow ? t('is_up_to_now') : t('new');
          if (candidate.inOfficeSince) {
            upToNow += ` (${t('since', {
              year: candidate.inOfficeSince,
            })})`;
          }

          const candidateVotes = candidate.votes
            ? t('formatted_number', { count: candidate.votes })
            : '-';

          const candidateArray: (string | number | JSX.Element)[] = [
            `${candidate.list} ${candidate.listName}`,
            candidate.candidateNumber.toString().padStart(2, '0'),
            `${candidate.lastName} ${candidate.firstName}`,
            upToNow,
            candidate.location,
            candidateVotes,
          ];

          if (showCountingCircleColumn) {
            candidateArray.unshift(candidate.countingCircle);
          }

          if (!showOnlyElected && showElectedColumn) {
            candidateArray.push(
              candidate.elected ? (
                <span className="proportionalelectiondetails__candidateresult--elected">
                  {t('yes')}
                </span>
              ) : (
                <span className="proportionalelectiondetails__candidateresult--notelected">
                  {t('no')}
                </span>
              ),
            );
          }

          return candidateArray;
        }),
    },
  };

  if (!showOnlyElected && showElectedColumn) {
    data.head.push({ title: t('elected'), width: 10, sortMethod: 'default' });
  }

  if (showCountingCircleColumn) {
    data.head.unshift({
      title: t('proportional_district'),
      width: 10,
      sortMethod: 'default',
    });
  }

  return {
    tableData: data,
    distinctLists,
    distinctDistricts,
  };
};
